import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableSkeleton, Amount, ListEmptyRow, StatCard, ListPagination } from '@redpanda/ui';
import { formatTableDate, useRefetchOnFocus, useRememberedState, useUrlPage, useSyncUrlPage } from '@redpanda/utils';
import { useReceivedPaymentsCache } from '../stores/pmReceivableStore';
import { useCrmCustomers, customerName } from '../stores/lookupStore';
import { useDataChanged } from '../utils/dataEvents';

type Period = 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'all';
const PERIODS: Period[] = ['thisMonth', 'lastMonth', 'thisYear', 'lastYear', 'all'];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 收款日區間（本地日期，含頭尾） */
function rangeOf(period: Period): { from?: string; to?: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (period) {
    case 'thisMonth': return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
    case 'lastMonth': return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    case 'thisYear': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'lastYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'all': return {};
  }
}

/**
 * 已收款：跨專案的收款記錄，依期間篩選
 * 摘要分開列實收與收款差額，扣繳另外顯示（個人工作室報稅對扣繳憑單用）
 */
const ReceivedPaymentsPanel: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [period, setPeriodState] = useRememberedState<Period>('pm-ui:receivables.receivedPeriod', 'thisMonth', PERIODS);
  // 頁碼存在網址 ?page=：點專案進詳情再返回時回到同一頁
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const [page, setPage] = useState(useUrlPage(urlPage));
  const setPeriod = (p: Period) => { setPeriodState(p); setPage(1); };
  const { data, refetch, key } = useReceivedPaymentsCache({ ...rangeOf(period), page });
  const customers = useCrmCustomers();
  const payments = data?.data ?? [];
  useSyncUrlPage(urlPage, data?.pagination, setPage);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, [key]);
  useDataChanged(['payments'], refetch);
  useRefetchOnFocus(refetch);

  const tp = (k: string, options?: Record<string, unknown>) => t(`pmReceivables.received.${k}`, { ns: 'pages', ...options });
  // 摘要由後端算整段期間（不是只有這一頁）
  const { received = 0, adjustments = 0, withholding = 0 } = data?.summary ?? {};
  const filterClass = (active: boolean) =>
    `inline-flex shrink-0 items-center rounded px-3 py-1.5 text-sm transition-colors ${active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'}`;

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard title={tp('summary.received')} value={<Amount value={received} />} hint={tp('summary.count', { count: data?.pagination.totalItems ?? 0 })} />
        <StatCard
          title={tp('summary.adjustments')}
          value={<Amount value={adjustments} />}
          hint={withholding > 0 ? tp('summary.withholding', { amount: withholding.toLocaleString() }) : undefined}
        />
      </div>

      <div className="mb-4">
        <nav aria-label={tp('periodLabel')} className="inline-flex h-10 max-w-full items-center overflow-x-auto scrollbar-hide rounded-md border border-line bg-surface p-0.5">
          {PERIODS.map(p => (
            <button key={p} type="button" onClick={() => setPeriod(p)} aria-pressed={period === p} className={filterClass(period === p)}>
              {tp(`period.${p}`)}
            </button>
          ))}
        </nav>
      </div>

      <ListPagination pagination={data?.pagination} onPageChange={setPage} className="mb-3" />
      {data === undefined ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead numeric className="w-32">{t('pmPayments.fields.paymentDate', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmReceivables.fields.project', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmReceivables.fields.milestone', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmPayments.fields.amount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-36">{t('pmPayments.fields.adjustmentAmount', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('pmPayments.fields.paymentMethod', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmPayments.fields.invoiceNumber', { ns: 'pages' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <ListEmptyRow colSpan={7} filtered={false} title={tp('empty')} />
                ) : (
                  payments.map(payment => (
                    <TableRow key={payment.id}>
                      <TableCell numeric>{formatTableDate(payment.paymentDate, i18n.language)}</TableCell>
                      <TableCell className="whitespace-normal">
                        <Link to={`/projects/${payment.project.id}?tab=payments`} className="font-medium text-fg hover:text-accent hover:underline">
                          {payment.project.name}
                        </Link>
                        {payment.project.customerId && (
                          <div className="mt-0.5 text-[13px] text-fg-secondary">{customerName(customers, payment.project.customerId)}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <div>{payment.milestone?.name ?? payment.package?.name ?? '—'}</div>
                        {payment.milestone && payment.package && <div className="mt-0.5 text-[13px] text-fg-secondary">{payment.package.name}</div>}
                      </TableCell>
                      <TableCell numeric className="font-semibold"><Amount value={payment.amount} /></TableCell>
                      <TableCell numeric>
                        {payment.adjustmentAmount ? (
                          <>
                            <Amount value={payment.adjustmentAmount} />
                            {payment.adjustmentType && (
                              <div className="mt-0.5 text-[13px] text-fg-secondary">{t(`pmPayments.adjustmentType.${payment.adjustmentType}`, { ns: 'pages' })}</div>
                            )}
                          </>
                        ) : <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell>{t(`pmPayments.paymentMethod.${payment.paymentMethod}`, { ns: 'pages' })}</TableCell>
                      <TableCell className="truncate tabular-nums">{payment.invoiceNumber || <span className="text-fg-muted">—</span>}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <ListPagination pagination={data?.pagination} onPageChange={setPage} className="mt-6" />
    </>
  );
};

export default ReceivedPaymentsPanel;
